import {
  isMrpIncomingTransferKind,
  type MrpIncomingRow,
} from "@/lib/mrp";
import { isMrpIncomingRowOpen } from "@integrator-src/mrp/incoming-row-open";

export const INCOMING_PEEK_ROW_LIMIT = 8;

export function incomingPeekDocumentLabel(row: MrpIncomingRow): string {
  if (row.kind === "work_order") {
    return row.work_order_mv_number ?? row.po_number ?? "Work order";
  }
  if (isMrpIncomingTransferKind(row.kind)) {
    return row.transfer_number ?? row.po_number ?? "Transfer";
  }
  return row.po_number ?? row.source ?? "Incoming";
}

export function incomingPeekKindLabel(row: MrpIncomingRow): string {
  if (row.kind === "po") return "PO";
  if (row.kind === "non_po") return "Manual";
  if (row.kind === "work_order") return "Work order";
  if (row.kind === "transfer_request") return "Transfer request";
  return "Transfer";
}

export function incomingPeekExpectedLabel(value: string | null): string {
  if (value == null || value.trim() === "" || value.startsWith("0001-") || value.startsWith("1900-")) {
    return "Unscheduled";
  }
  const date = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function compareIncomingPeekRows(left: MrpIncomingRow, right: MrpIncomingRow): number {
  const leftDate = left.expected_arrival ?? "";
  const rightDate = right.expected_arrival ?? "";
  if (leftDate !== rightDate) {
    if (leftDate === "") return 1;
    if (rightDate === "") return -1;
    return leftDate.localeCompare(rightDate);
  }
  return incomingPeekDocumentLabel(left).localeCompare(incomingPeekDocumentLabel(right));
}

/**
 * Rows shown in the incoming peek hover.
 * SKU-only (Overstock): purchase orders, manual arrivals, and work orders.
 * Origin-site scoped (Transfers origin qty): same as Incoming parts defaults, plus
 * transfer legs arriving at that site (`transfer_role = "to"`).
 */
export function filterIncomingPeekRows(
  rows: readonly MrpIncomingRow[],
  locationCode?: string | null,
): MrpIncomingRow[] {
  const site = locationCode?.trim().toLowerCase() ?? "";
  return [...rows]
    .filter((row) => isMrpIncomingRowOpen(row))
    .filter((row) => {
      if (site === "") {
        return !isMrpIncomingTransferKind(row.kind);
      }
      if (row.kind === "work_order") return false;
      if (isMrpIncomingTransferKind(row.kind) && row.transfer_role !== "to") return false;
      return true;
    })
    .sort(compareIncomingPeekRows);
}
